import { useState, useEffect, useRef, useCallback, useMemo, type JSX } from 'react';
import type { StepSchema, LineSpeaker, LineMessage, RunState } from 'shared';
import { getEffect, clearActivatedEffects, useInputHijack, normalizeInputHijackParams } from './effects';
import { resolveMediaRefToUrl } from '../../utils/mediaRef';
import { filterChoicesByCondition } from '../../engine/choiceCondition';
import { getTimeOfDay } from '../../atmosphere/timeOfDay';

type LineDialogStep = Extract<StepSchema, { type: 'line_dialog' }>;

interface AiReplyData {
  userText: string;
  routeKey: string;
  aiReplyLines: string[];
}

interface LineDialogViewProps {
  step: LineDialogStep;
  state?: RunState | null;
  onChoiceSelect?: (optionIndex?: number) => void;
  onFreeInputSubmit?: (text: string) => void;
  onComplete?: () => void;
  /**
   * P7-4 panel presentation 専用フック（fullscreen では未使用）。
   * 新しいメッセージが表示されるたびに、そのメッセージの発信者 side（'other'|'self'）と
   * 現在の累計表示数を通知する。パネル外側のシェルが「閉鎖中の未読バッジ」を
   * 加算するために使う。LineDialogView 内部の visibleCount ステート機械は一切変更しない
   * （既存の逐次表示 useEffect の deps・タイマーに触れない、純粋な副作用の追加報告のみ）。
   */
  onMessageRevealed?: (info: { side: 'other' | 'self'; totalVisible: number }) => void;
  /**
   * P7-4 panel presentation 専用フック。返信（choice/free input）または自動送りタップの
   * 「操作待ち」状態が発生/解消するたびに通知する。パネルが閉じている間、外側シェルが
   * バッジをパルスさせて呼び戻すために使う。
   */
  onWaitingForActionChange?: (waiting: boolean) => void;
  /**
   * P7-4 panel presentation 専用。true のとき自前のヘッダー（緑バー+チャットタイトル）を
   * 描画しない。LineDialogPanelShell が独自の閉じるボタン付きヘッダーを持つため、
   * 二重ヘッダー表示を避ける。fullscreen（既定 false）では従来どおり表示する。
   */
  hideHeader?: boolean;
}

function resolveLineMediaRef(ref?: string): string | undefined {
  if (!ref) return undefined;
  return resolveMediaRefToUrl(ref) ?? ref;
}

export function LineDialogView({ step, state, onChoiceSelect, onFreeInputSubmit, onComplete, onMessageRevealed, onWaitingForActionChange, hideHeader }: LineDialogViewProps): JSX.Element {
  const [visibleCount, setVisibleCount] = useState(0);
  const [typingVisible, setTypingVisible] = useState(false);
  const [replyEnabled, setReplyEnabled] = useState(false);
  const [selectedReply, setSelectedReply] = useState<number | null>(null);
  const [freeInputText, setFreeInputText] = useState('');
  const [freeInputSubmitted, setFreeInputSubmitted] = useState(false);
  const [aiReplyPhase, setAiReplyPhase] = useState<'idle' | 'showing_user' | 'showing_ai' | 'done'>('idle');
  const [aiVisibleCount, setAiVisibleCount] = useState(0);
  const [aiTypingVisible, setAiTypingVisible] = useState(false);
  // aiReplyPhase 専用 ref: AI返信トリガー useEffect 内で「現在 idle か」をチェックするために使う。
  // state 直読みだと aiReplyPhase を deps に入れる必要が出て、setAiReplyPhase('showing_user') で
  // 即再 run→cleanup で setTimeout 破棄→showing_ai に遷移できず固まる、というバグになる。
  const aiReplyPhaseRef = useRef(aiReplyPhase);
  useEffect(() => {
    aiReplyPhaseRef.current = aiReplyPhase;
  }, [aiReplyPhase]);
  // Phase A-k: 既読アニメーション & メッセージ取り消し
  const [readShown, setReadShown] = useState<Set<number>>(new Set());
  const [recalledMessages, setRecalledMessages] = useState<Set<number>>(new Set());
  // 新着メッセージで最下部へスクロールする対象は .line-dialog__chat 内部のみに限定する。
  // scrollIntoView は「全 scrollable 祖先」を動かすため、ページ(body/#root)が縦に溢れていると
  // チャット欄でなくページ全体がジャンプして通知が見切れる。内部コンテナを直接 scrollTo する。
  const chatListRef = useRef<HTMLDivElement>(null);

  const messages = step.messages;
  const speakers = step.speakers ?? {};

  // P4-2 深夜既読: 再生開始時の端末ローカル時刻で深夜帯か一度だけ確定する（途中で日付を跨いでも
  // 演出が点滅しないよう mount 時固定）。深夜でなければ保留は一切起きない（通常動作）。
  const isDeepNight = useMemo(() => getTimeOfDay().isDeepNight, []);

  // 深夜のとき、deepNightReadHold:true の最初のメッセージを「保留点」とする。
  // そのメッセージまでは表示する（＝既読がつく）が、それ以降のメッセージ・返信欄・タイピングは出さない。
  // holdIndex は「保留メッセージの index」。null なら保留なし（= messages.length を上限に通常進行）。
  const holdIndex = useMemo(() => {
    if (!isDeepNight) return null;
    const i = messages.findIndex((m) => m.deepNightReadHold === true);
    return i >= 0 ? i : null;
  }, [isDeepNight, messages]);

  // 表示してよいメッセージ数の上限。保留時は holdIndex までで打ち切る（保留メッセージ自身は表示）。
  const effectiveMessageCount = holdIndex == null ? messages.length : holdIndex + 1;
  // 保留が発動したら返信欄を出さない（「読んだのに返ってこない」状態でユーザー操作を止める）。
  const isDeepNightHeld = holdIndex != null;

  // R3: reply options を condition で絞る。表示するこの配列の index が
  // そのまま engine の selectLineDialogChoice へ渡る。engine 側も同じ
  // filterChoicesByCondition を通すため index は一致する（ズレ事故を防ぐ）。
  // state が無い場合（プレビュー等）は絞らず全件表示する。
  const visibleReplyOptions = useMemo(() => {
    const options = step.reply?.options;
    if (!options) return [];
    return state ? filterChoicesByCondition(options, state) : options;
  }, [step.reply?.options, state]);

  // ノード切り替え時にエフェクト永続化ストアをクリア
  useEffect(() => {
    clearActivatedEffects();
    return () => clearActivatedEffects();
  }, []);

  const aiReplyData = state?.flags?.__lineDialogAiReply as AiReplyData | undefined;

  // P4-1 input_hijack: 表示済みメッセージの中に effectId 'input_hijack' があれば freeInput を arm する。
  // 他の超常演出は吹き出しを wrap するが、これだけは唯一 freeInput に作用する（spec §3.3 / category 'input'）。
  // params はそのメッセージの supernaturalEffect.params から読み、spec 既定値で補完する（demo が上書き）。
  const hijackParams = useMemo(() => {
    const armedMsg = messages
      .slice(0, visibleCount)
      .find((m) => m.supernaturalEffect?.effectId === 'input_hijack');
    if (!armedMsg) return null;
    return normalizeInputHijackParams(armedMsg.supernaturalEffect?.params);
  }, [messages, visibleCount]);

  const hijack = useInputHijack({
    armed: hijackParams != null,
    params: hijackParams ?? normalizeInputHijackParams(undefined),
    setValue: setFreeInputText,
  });

  // メッセージを順番にアニメーション表示
  useEffect(() => {
    // P4-2: 深夜保留時は effectiveMessageCount（保留点まで）で打ち切る。
    // 上限に達したら、保留中は返信欄を出さない（既読のまま沈黙）。通常時のみ replyEnabled。
    if (visibleCount >= effectiveMessageCount) {
      if (isDeepNightHeld) return; // 保留: タイピングも返信欄も出さず、既読だけ残す。
      const delay = step.autoAdvance ? (step.autoAdvanceDelayMs ?? 2000) : 500;
      const timer = setTimeout(() => setReplyEnabled(true), delay);
      return () => clearTimeout(timer);
    }

    const msg = messages[visibleCount];
    // タイムスタンプはタイピングインジケータ不要、即座に表示
    if (msg.kind === 'timestamp') {
      const timer = setTimeout(() => {
        setVisibleCount((c) => c + 1);
      }, msg.delayMs ?? 300);
      return () => clearTimeout(timer);
    }

    const typingMs = msg.typingIndicatorMs ?? 1500;
    const delayMs = msg.delayMs ?? 800;

    const showTypingTimer = setTimeout(() => {
      setTypingVisible(true);
    }, delayMs);

    const showMessageTimer = setTimeout(() => {
      setTypingVisible(false);
      setVisibleCount((c) => c + 1);
    }, delayMs + typingMs);

    return () => {
      clearTimeout(showTypingTimer);
      clearTimeout(showMessageTimer);
    };
  }, [visibleCount, messages, effectiveMessageCount, isDeepNightHeld, step.autoAdvance, step.autoAdvanceDelayMs]);

  // Phase A-k: 既読遅延表示
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    messages.slice(0, visibleCount).forEach((msg, i) => {
      if (msg.showRead && !readShown.has(i)) {
        const delay = msg.readDelayMs ?? 0;
        const t = setTimeout(() => {
          setReadShown((prev) => new Set(prev).add(i));
        }, delay);
        timers.push(t);
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [visibleCount, messages, readShown]);

  // Phase A-k: メッセージ取り消しタイマー
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    messages.slice(0, visibleCount).forEach((msg, i) => {
      if (msg.recallAfterMs != null && msg.recallAfterMs > 0 && !recalledMessages.has(i)) {
        const t = setTimeout(() => {
          setRecalledMessages((prev) => new Set(prev).add(i));
        }, msg.recallAfterMs);
        timers.push(t);
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [visibleCount, messages, recalledMessages]);

  // AI返信データが到着したらアニメーション開始
  // 注意: aiReplyPhase を deps に入れない。入れると setAiReplyPhase('showing_user') で
  // 再 run され cleanup が setTimeout を clearTimeout してしまい、showing_ai に永久に
  // 遷移できず「応答を待っています...」で固まる（既存潜在バグ・soma/qa が line_dialog
  // freeInput を踏まなかったため未発覚だった）。
  useEffect(() => {
    if (!aiReplyData || !freeInputSubmitted) return;
    if (aiReplyPhaseRef.current !== 'idle') return;
    setAiReplyPhase('showing_user');
    const timer = setTimeout(() => {
      setAiReplyPhase('showing_ai');
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiReplyData, freeInputSubmitted]);

  // AI返信のアニメーション
  useEffect(() => {
    if (aiReplyPhase !== 'showing_ai' || !aiReplyData) return;
    const lines = aiReplyData.aiReplyLines;
    if (aiVisibleCount >= lines.length) {
      const timer = setTimeout(() => {
        setAiReplyPhase('done');
        onComplete?.();
      }, 1200);
      return () => clearTimeout(timer);
    }

    const showTypingTimer = setTimeout(() => setAiTypingVisible(true), 600);
    const showMessageTimer = setTimeout(() => {
      setAiTypingVisible(false);
      setAiVisibleCount((c) => c + 1);
    }, 600 + 1200);

    return () => {
      clearTimeout(showTypingTimer);
      clearTimeout(showMessageTimer);
    };
  }, [aiReplyPhase, aiVisibleCount, aiReplyData, onComplete]);

  // 自動スクロール（内部チャット欄限定）
  // chatListRef.current（=.line-dialog__chat）だけを最下部へ送る。ページや返信欄は動かさない。
  useEffect(() => {
    const el = chatListRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [visibleCount, typingVisible, aiReplyPhase, aiVisibleCount, aiTypingVisible]);

  // P7-4: panel presentation の未読バッジ用の純粋な報告 副作用。
  // 既存の逐次表示 useEffect（visibleCount を進める本体）には一切触れず、
  // visibleCount が増えたことを外側へ知らせるだけ（読み取り専用）。
  // fullscreen（onMessageRevealed 未指定）では何もしない。
  const prevVisibleCountRef = useRef(0);
  useEffect(() => {
    if (!onMessageRevealed) return;
    const prev = prevVisibleCountRef.current;
    if (visibleCount > prev) {
      for (let i = prev; i < visibleCount; i += 1) {
        const msg = messages[i];
        if (!msg || msg.kind === 'timestamp') continue;
        // getSpeaker はこの関数コンポーネント内でこの後 const 定義されるため
        // （TDZ 回避）、ここでは speakers を直接引く同等ロジックをインライン化する。
        const side = speakers[msg.senderKey]?.side ?? 'other';
        onMessageRevealed({ side, totalVisible: visibleCount });
      }
    }
    prevVisibleCountRef.current = visibleCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCount, onMessageRevealed]);

  // P7-4: 「操作待ち」状態（choice/free input の返信待ち、または自動送りのタップ待ち）を
  // 外側へ報告する。判定は既存の描画条件式（下の JSX）と同一のものを使う（読み取り専用）。
  const isWaitingForReply =
    replyEnabled && Boolean(step.reply) && !step.autoAdvance && aiReplyPhase === 'idle' && selectedReply === null;
  const isWaitingForAutoAdvanceTap = replyEnabled && step.autoAdvance;
  const isWaitingForAction = isWaitingForReply || isWaitingForAutoAdvanceTap;
  useEffect(() => {
    onWaitingForActionChange?.(isWaitingForAction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWaitingForAction, onWaitingForActionChange]);

  const handleChoiceClick = useCallback((index: number) => {
    if (selectedReply !== null) return;
    setSelectedReply(index);
    onChoiceSelect?.(index);
  }, [selectedReply, onChoiceSelect]);

  const handleFreeInputSend = useCallback(() => {
    const trimmed = freeInputText.trim();
    if (!trimmed) return;
    setSelectedReply(-1);
    setFreeInputSubmitted(true);
    onFreeInputSubmit?.(trimmed);
  }, [freeInputText, onFreeInputSubmit]);

  const getSpeaker = (senderKey: string): LineSpeaker | undefined => speakers[senderKey];

  const getFirstOtherSpeaker = (): { key: string; speaker: LineSpeaker } | undefined => {
    for (const [key, speaker] of Object.entries(speakers)) {
      if (speaker.side === 'other') return { key, speaker };
    }
    return undefined;
  };

  const nextTypingSender = visibleCount < messages.length ? getSpeaker(messages[visibleCount].senderKey) : undefined;
  const aiSpeaker = getFirstOtherSpeaker();

  return (
    <div className="line-dialog" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header（P7-4: panel presentation では LineDialogPanelShell 側のヘッダーと二重になるため非表示） */}
      {!hideHeader && (
        <div className="line-dialog__header" style={{ backgroundColor: step.theme?.headerColor ?? '#06C755' }}>
          <span className="line-dialog__header-back">{'<'}</span>
          {step.chatProfileImage && (
            <img className="line-dialog__header-avatar" src={resolveLineMediaRef(step.chatProfileImage)} alt="" />
          )}
          <span className="line-dialog__header-title">{step.chatTitle}</span>
        </div>
      )}

      {/* Chat Area */}
      <div
        ref={chatListRef}
        className="line-dialog__chat"
        style={{ backgroundColor: step.theme?.chatBgColor ?? '#8CAAB9' }}
      >
        {messages.slice(0, visibleCount).map((msg, i) => {
          // タイムスタンプは特別な区切り要素
          if (msg.kind === 'timestamp') {
            return <TimestampDivider key={`ts-${i}`} text={msg.text} />;
          }

          const speaker = getSpeaker(msg.senderKey);
          const side = speaker?.side ?? 'other';
          const isRecalled = recalledMessages.has(i);
          const isReadShown = readShown.has(i);

          const bubble = (
            <LineBubble
              key={`msg-${i}`}
              message={msg}
              senderName={speaker?.displayName ?? msg.senderKey}
              side={side}
              profileImage={speaker?.profileImage}
              bubbleColor={speaker?.bubbleColor}
              bubbleTextColor={speaker?.bubbleTextColor}
              isRecalled={isRecalled}
              recallDisplayText={msg.recallDisplayText}
              showRead={msg.showRead && side === 'self' && isReadShown}
            />
          );
          return wrapWithEffect(bubble, msg, `msg-${i}`);
        })}

        {/* Typing Indicator */}
        {typingVisible && nextTypingSender && (
          <div className="line-dialog__typing-row">
            {nextTypingSender.profileImage && (
              <img className="line-dialog__typing-avatar" src={resolveLineMediaRef(nextTypingSender.profileImage)} alt="" />
            )}
            <div className="line-dialog__typing">
              <span className="line-dialog__typing-name">{nextTypingSender.displayName}</span>
              <div className="line-dialog__typing-dots">
                <span className="line-dialog__typing-dot" />
                <span className="line-dialog__typing-dot" />
                <span className="line-dialog__typing-dot" />
              </div>
            </div>
          </div>
        )}

        {/* AI返信フロー: ユーザーメッセージ */}
        {aiReplyData && aiReplyPhase !== 'idle' && (
          <LineBubble
            message={{ senderKey: '_self', text: aiReplyData.userText, kind: 'text' } as LineMessage}
            senderName="あなた"
            side="self"
          />
        )}

        {/* AI返信フロー: AI返信メッセージ */}
        {aiReplyData && aiReplyPhase === 'showing_ai' && (
          <>
            {aiReplyData.aiReplyLines.slice(0, aiVisibleCount).map((line, i) => (
              <LineBubble
                key={`ai-${i}`}
                message={{ senderKey: aiSpeaker?.key ?? '_ai', text: line, kind: 'text' } as LineMessage}
                senderName={aiSpeaker?.speaker?.displayName ?? 'AI'}
                side="other"
                profileImage={aiSpeaker?.speaker?.profileImage}
              />
            ))}
            {aiTypingVisible && (
              <div className="line-dialog__typing-row">
                {aiSpeaker?.speaker?.profileImage && (
                  <img className="line-dialog__typing-avatar" src={resolveLineMediaRef(aiSpeaker.speaker.profileImage)} alt="" />
                )}
                <div className="line-dialog__typing">
                  <span className="line-dialog__typing-name">{aiSpeaker?.speaker?.displayName ?? 'AI'}</span>
                  <div className="line-dialog__typing-dots">
                    <span className="line-dialog__typing-dot" />
                    <span className="line-dialog__typing-dot" />
                    <span className="line-dialog__typing-dot" />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* AI返信完了後も表示を維持 */}
        {aiReplyData && aiReplyPhase === 'done' && (
          <>
            {aiReplyData.aiReplyLines.map((line, i) => (
              <LineBubble
                key={`ai-done-${i}`}
                message={{ senderKey: aiSpeaker?.key ?? '_ai', text: line, kind: 'text' } as LineMessage}
                senderName={aiSpeaker?.speaker?.displayName ?? 'AI'}
                side="other"
                profileImage={aiSpeaker?.speaker?.profileImage}
              />
            ))}
          </>
        )}
      </div>

      {/* Reply Area */}
      {replyEnabled && step.reply && !step.autoAdvance && aiReplyPhase === 'idle' && (
        <div className="line-dialog__reply">
          {selectedReply === null ? (
            <>
              <div className="line-dialog__choices">
                {visibleReplyOptions.map((opt, i) => (
                  <button
                    key={i}
                    className="line-dialog__choice-btn"
                    onClick={() => handleChoiceClick(i)}
                  >
                    {opt.text}
                  </button>
                ))}
              </div>
              {step.reply.mode === 'choices_and_free_input' && step.reply.freeInput && (
                <div className="line-dialog__free-input">
                  <input
                    type="text"
                    value={freeInputText}
                    onChange={hijack.inputProps.onChange}
                    onCompositionStart={hijack.inputProps.onCompositionStart}
                    onCompositionEnd={hijack.inputProps.onCompositionEnd}
                    placeholder={step.reply.freeInput.placeholder ?? 'メッセージを入力...'}
                    maxLength={step.reply.freeInput.maxChars ?? 200}
                    className={`line-dialog__free-input-field${hijack.shouldShake ? ' sn-input-hijack-shake' : ''}`}
                    aria-label="メッセージを入力"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleFreeInputSend(); }}
                  />
                  <button
                    className="line-dialog__send-btn"
                    onClick={handleFreeInputSend}
                    disabled={!freeInputText.trim()}
                  >
                    送信
                  </button>
                </div>
              )}
            </>
          ) : !freeInputSubmitted ? (
            <div className="line-dialog__reply-sent"><p>返信しました</p></div>
          ) : (
            <div className="line-dialog__reply-sent"><p>送信中...</p></div>
          )}
        </div>
      )}

      {freeInputSubmitted && aiReplyPhase !== 'idle' && aiReplyPhase !== 'done' && (
        <div className="line-dialog__reply">
          <div className="line-dialog__reply-sent"><p>応答を待っています...</p></div>
        </div>
      )}

      {replyEnabled && step.autoAdvance && (
        <div className="line-dialog__auto-advance" onClick={() => onChoiceSelect?.()}>
          <p>タップで次へ</p>
        </div>
      )}
    </div>
  );
}

// ============================
// タイムスタンプ区切り
// ============================
function TimestampDivider({ text }: { text: string }) {
  return (
    <div className="line-timestamp">
      <span className="line-timestamp__text">{text}</span>
    </div>
  );
}

// ============================
// 超常演出ラッパー
// ============================
function wrapWithEffect(bubble: JSX.Element, msg: LineMessage, key: string): JSX.Element {
  const effectConfig = msg.supernaturalEffect;
  if (!effectConfig) return bubble;

  const effect = getEffect(effectConfig.effectId);
  if (!effect) return bubble;

  const EffectComponent = effect.component;
  return (
    <EffectComponent
      key={`effect-${key}`}
      effectKey={`effect-${key}`}
      originalContent={{ text: msg.text, senderKey: msg.senderKey }}
      params={{ ...effect.defaultParams, ...effectConfig.params }}
      trigger={effectConfig.trigger ?? 'on_appear'}
      triggerDelayMs={effectConfig.triggerDelayMs ?? 0}
    >
      {bubble}
    </EffectComponent>
  );
}

// ============================
// LineBubble: 全kind対応
// ============================
interface LineBubbleProps {
  message: LineMessage;
  senderName: string;
  side: 'other' | 'self';
  profileImage?: string;
  bubbleColor?: string;
  bubbleTextColor?: string;
  isRecalled?: boolean;
  recallDisplayText?: string;
  showRead?: boolean;
}

function LineBubble({
  message,
  senderName,
  side,
  profileImage,
  bubbleColor,
  bubbleTextColor,
  isRecalled,
  recallDisplayText,
  showRead
}: LineBubbleProps) {
  const isOther = side === 'other';
  const bgColor = bubbleColor ?? (isOther ? '#FFFFFF' : '#06C755');
  const textColor = bubbleTextColor ?? (isOther ? '#333333' : '#FFFFFF');
  const kind = message.kind ?? 'text';
  const resolvedProfileImage = resolveLineMediaRef(profileImage);

  // 取り消されたメッセージ
  if (isRecalled) {
    return (
      <div className={`line-bubble line-bubble--${side}`}>
        {isOther && resolvedProfileImage && (
          <img className="line-bubble__avatar" src={resolvedProfileImage} alt="" />
        )}
        <div className="line-bubble__content">
          {isOther && <span className="line-bubble__name">{senderName}</span>}
          <div className="line-bubble__recalled">
            {recallDisplayText ?? 'メッセージの送信が取り消されました'}
          </div>
        </div>
      </div>
    );
  }

  // メッセージ本体のレンダリング（kind別）
  const renderBody = () => {
    switch (kind) {
      case 'image':
        return (
          <div className="line-bubble__image-wrap">
            {message.imageAssetId ? (
              <img className="line-bubble__image" src={resolveLineMediaRef(message.imageAssetId)} alt={message.text} />
            ) : (
              <div className="line-bubble__image-placeholder">📷 {message.text}</div>
            )}
          </div>
        );

      case 'video':
        return (
          <div className="line-bubble__video-wrap">
            {message.videoAssetId ? (
              <VideoMessage
                src={resolveLineMediaRef(message.videoAssetId)}
                poster={resolveLineMediaRef(message.imageAssetId)}
                trapEffect={message.videoTrapEffect}
              />
            ) : (
              <div className="line-bubble__video-placeholder">🎬 {message.text}</div>
            )}
          </div>
        );

      case 'audio':
        return (
          <div className="line-bubble__audio-wrap" style={{ backgroundColor: bgColor, color: textColor }}>
            <AudioMessage
              src={resolveLineMediaRef(message.audioAssetId)}
              durationSec={message.audioDurationSec}
            />
          </div>
        );

      case 'stamp':
        return (
          <div className="line-bubble__stamp-wrap">
            {message.imageAssetId ? (
              <img className="line-bubble__stamp" src={resolveLineMediaRef(message.imageAssetId)} alt={message.text} />
            ) : (
              <div className="line-bubble__stamp-placeholder">{message.text}</div>
            )}
          </div>
        );

      case 'location':
        return (
          <div className="line-bubble__location" style={{ backgroundColor: bgColor, color: textColor }}>
            <div className="line-bubble__location-map">
              📍
            </div>
            <div className="line-bubble__location-info">
              <span className="line-bubble__location-name">
                {message.location?.name ?? message.text}
              </span>
              {message.location?.address && (
                <span className="line-bubble__location-address">{message.location.address}</span>
              )}
            </div>
          </div>
        );

      case 'text':
      default:
        return (
          <div
            className="line-bubble__body"
            style={{ backgroundColor: bgColor, color: textColor }}
          >
            {message.text}
          </div>
        );
    }
  };

  return (
    <div className={`line-bubble line-bubble--${side}`}>
      {isOther && resolvedProfileImage && (
        <img className="line-bubble__avatar" src={resolvedProfileImage} alt="" />
      )}
      <div className="line-bubble__content">
        {isOther && <span className="line-bubble__name">{senderName}</span>}
        {renderBody()}
      </div>
      {/* 既読表示 */}
      {showRead && side === 'self' && (
        <span className="line-bubble__read">既読</span>
      )}
    </div>
  );
}

// ============================
// 動画メッセージ（トラップ演出対応）
// ============================
function VideoMessage({
  src,
  poster,
  trapEffect
}: {
  src?: string;
  poster?: string;
  trapEffect?: LineMessage['videoTrapEffect'];
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [trapFired, setTrapFired] = useState(false);

  const fireTrap = useCallback(() => {
    if (trapFired || !trapEffect) return;
    setTrapFired(true);
    // トラップ演出発動 — screen_distortion等を画面全体に適用
    const effect = getEffect(trapEffect.effectId);
    if (effect) {
      // 画面全体にオーバーレイを追加
      const overlay = document.createElement('div');
      overlay.className = 'line-video-trap-overlay';
      overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;pointer-events:none;';
      document.body.appendChild(overlay);
      setTimeout(() => overlay.remove(), 3000);
    }
  }, [trapEffect, trapFired]);

  const handlePlay = () => {
    setPlaying(true);
    videoRef.current?.play();
    if (trapEffect?.trigger === 'on_play') {
      fireTrap();
    }
  };

  useEffect(() => {
    if (!playing || !trapEffect || trapEffect.trigger !== 'at_time') return;
    const sec = trapEffect.triggerAtSec ?? 0;
    const interval = setInterval(() => {
      if (videoRef.current && videoRef.current.currentTime >= sec) {
        fireTrap();
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [playing, trapEffect, fireTrap]);

  if (!src) return <div className="line-bubble__video-placeholder">🎬</div>;

  return (
    <div className="line-bubble__video-container" onClick={!playing ? handlePlay : undefined}>
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        className="line-bubble__video"
        playsInline
        onEnded={() => {
          if (trapEffect?.trigger === 'on_end') fireTrap();
        }}
      />
      {!playing && (
        <div className="line-bubble__video-play-btn">▶</div>
      )}
    </div>
  );
}

// ============================
// 音声メッセージ
// ============================
function AudioMessage({
  src,
  durationSec
}: {
  src?: string;
  durationSec?: number;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => {
      if (audio.duration) setProgress(audio.currentTime / audio.duration);
    };
    const onEnded = () => { setPlaying(false); setProgress(0); };
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const displayDuration = durationSec ?? 0;
  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="line-bubble__audio" onClick={togglePlay}>
      {src && <audio ref={audioRef} src={src} preload="metadata" />}
      <span className="line-bubble__audio-icon">{playing ? '⏸' : '▶'}</span>
      <div className="line-bubble__audio-bar">
        <div className="line-bubble__audio-progress" style={{ width: `${progress * 100}%` }} />
      </div>
      <span className="line-bubble__audio-duration">{formatTime(displayDuration)}</span>
    </div>
  );
}

export default LineDialogView;
